import { Config, UIHelper as uh } from 'src/core';
import React, { useCallback, useEffect, useState } from 'react';
import { useGetDataSharingList, useGetUserAttributes, useUser } from 'src/core/hooks/useUser';
import { useLatestDataCount, useUpdateLatestDataCount } from 'src/core/hooks/useLatestDataCount';
import ActionIcon from 'src/features/menu/components/icons/ActionIcon';
import AssessmentIcon from 'src/features/menu/components/icons/AssessmentIcon';
import BottomMenu from 'src/features/menu/BottomMenu';
import ConsolidateHealthService from 'src/api/consolidateHealth';
import { ErrorModal } from 'src/components/shared/ErrorModal';
import GroupMenu from './GroupMenu';
import HealthRecordsIcon from 'src/features/menu/components/icons/HealthRecordsIcon';
import { LatestDataCountType } from 'src/core/types/UserAttributes';
import MarkerIcon from 'src/features/menu/components/icons/MarkerIcon';
import ScanIcon from 'src/features/menu/components/icons/ScanIcon';
import { ScrollView } from 'react-native-gesture-handler';
import SpecialGroupMenu from 'src/features/menu/SpecialGroupMenu';
import Spinner from 'react-native-loading-spinner-overlay/lib';
import { Platform, StyleSheet, ViewProps } from 'react-native';
import TalkToKaliIcon from 'src/features/menu/components/icons/TalkToKaliIcon';
import { mainMenuContainer } from 'src/core/brands';
import { useAppStore } from 'src/core/store';
import { useGetAppName, useGetChatbotName } from 'src/core/hooks/useBrands';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '@ui-kitten/components';
import { useTranslationFunc } from 'src/core/hooks/useTranslationFunc';
import { useSetFirstRun } from 'src/core/hooks/useGlobalVar';
import { fetchAuthSession } from '@aws-amplify/auth';
import { InfoModal } from 'src/components/shared/InfoModal';
import { BackendApi } from 'src/api/shared';
import { CHPatientMappingDto } from 'src/core/types/ConsolidateHealth';

export type GroupMenuItem = {
  name: string;
  id: string;
  action: () => any;
  hidden: boolean;
  leftIcon?: any;
  count?: number;
  accessoryLeft?: string;
  status?: string;
};

//styles
const styleContainer = StyleSheet.create({
  scrollView: {
    flex: 1,
    paddingLeft: uh.w2DP(16),
    paddingRight: uh.w2DP(16),
    paddingTop: uh.h2DP(16),
    paddingBottom: uh.h2DP(20)
  }
});

//props
interface IMainMenuContainerProps extends ViewProps {
  code?: string;
}

const MainMenuContainer = (props: IMainMenuContainerProps) => {
  const appName = useGetAppName();
  const navigation = useNavigation();
  const newAssessmentCount = useAppStore((state) => state.newAssessmentCount);
  const newActionCount = useAppStore((state) => state.newActionCount);
  const newMessageCount = useAppStore((state) => state.newMessageCount);
  const userAttributes = useGetUserAttributes();
  const [latestDataCountRefreshing, setLatestDataCountRefreshing] = useState<boolean>(false);
  const [isLatestDataCountLoading, latestDataCountError] = useLatestDataCount({
    refreshing: latestDataCountRefreshing,
    setRefreshing: setLatestDataCountRefreshing
  });

  const [visibleAlreadyConnectedCHModal, setVisibleAlreadyConnectedCHModal] = useState<boolean>(false);
  const [visibleConnectedSuccesfulCHModal, setVisibleConnectedSuccesfulCHModal] = useState<boolean>(false);

  // const [patientMapping, setPatientPapping] = useState<CHPatientMappingDto>();
  // const tenantFeatures: TenantFeature[] = useGetTenantFeatures();

  const patientMapping = useAppStore((state) => state.patientMapping);
  const setPatientMapping = useAppStore((state) => state.setPatientMapping);

  const [updateLatestDataCount, updateLatestDataCountError] = useUpdateLatestDataCount();
  const th = useTheme();

  const { signOut } = useUser();
  //properties and handler
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const actionsHandler = () => {
    if (newActionCount > 0) {
      updateLatestDataCount(LatestDataCountType.ACTION);
    }
    navigation.navigate('Actions', { screen: 'ActionProtocolTab' });
  };
  const scanHandler = () => navigation.navigate('ScanGettingStarted');
  const assessmentsHandler = () => {
    if (newAssessmentCount > 0) {
      updateLatestDataCount(LatestDataCountType.ASSESSMENT);
    }
    navigation.navigate('Assessments');
  };
  const markersHandler = () => {
    updateLatestDataCount(LatestDataCountType.MARKER);
    navigation.navigate('MyMarkers', { screen: 'Favorite' });
  };
  const healthRecordsHandler = () => navigation.navigate('HealthRecords');
  const chatHandler = () => {
    if (newMessageCount > 0) {
      updateLatestDataCount(LatestDataCountType.KALI);
    }
    navigation.navigate('Kali');
  };
  const profileHandler = () => navigation.navigate('ProfileSettings');
  // const subscriptionsHandler = () => navigation.navigate('Subscriptions');
  const accountHandler = () => null;
  const dataSharingHandler = () => navigation.navigate('DataSharing');
  const wearablesHandler = () => navigation.navigate('ConnectedDevices');
  const preferencesHandler = () => navigation.navigate('Preferences');
  const developerSettingsHandler = () => navigation.navigate('DeveloperSettings');
  const aboutUsHandler = () => navigation.navigate('AboutUs');
  const feedbackHandler = () => navigation.navigate('Feedback');
  const signOutHandler = signOut;
  const isDataSharingEnabled = useGetDataSharingList().length > 0;
  const setFirstLoad = useSetFirstRun();

  const handleSignOut = () => {
    setIsLoading(true);
    setFirstLoad(false);
    signOutHandler();
  };

  // Helper function to generate cryptographically secure random string
  const generateStateParameter = (length = 32) => {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let result = '';
    const randomValues = new Uint8Array(length);

    // Generate random values
    window.crypto.getRandomValues(randomValues);

    // Map random values to the charset
    randomValues.forEach((value) => {
      result += charset[value % charset.length];
    });

    return result;
  };

  const loadPatientMapping = useCallback(async () => {
    try {
      const patientMapping = await ConsolidateHealthService.getPatientMapping();
      setPatientMapping(patientMapping);
    } catch (err) {
      console.error(err);
    }
  }, [setPatientMapping]);

  React.useEffect(() => {
    loadPatientMapping();
  }, [loadPatientMapping]);

  useEffect(() => {
    const exchangeCHToken = (code: string) => {
      if (code.length > 0) {
        BackendApi.post('/users/consolidate-health/token', {
          code: code,
          redirectUri:
            Platform.OS == 'web' ? Config.CONNECT_HEALTH_RECORDS_CALLBACK_WEB : Config.CONNECT_HEALTH_RECORDS_CALLBACK
        })
          .then(async (response) => {
            if (response.data && response.data.refresh_token) {
              setPatientMapping(response.data as CHPatientMappingDto);
              setVisibleConnectedSuccesfulCHModal(true);
            }
          })
          .catch((error) => {
            console.error(error);
          });
      }
    };
    const callbackCode = props.code;
    if (callbackCode && callbackCode.length > 0) {
      exchangeCHToken(callbackCode);
    }
  }, [setPatientMapping, props.code]);

  const connectHealthRecordsHandler = async () => {
    if (patientMapping?.userId != undefined) {
      setVisibleAlreadyConnectedCHModal(true);
      return;
    }
    const state = generateStateParameter();
    const { tokens } = await fetchAuthSession();
    const attributes = tokens?.idToken?.payload ?? {};

    const pEmail = attributes['custom:public_email'] || '';
    let firstName = attributes.given_name ?? '';
    const lastName = attributes.family_name ?? 'unknown';
    if (String(firstName).length == 0 && String(lastName) == 'unknown') {
      firstName = String(attributes.nickname);
    }

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: Config.CONNECT_HEALTH_RECORDS_CLIENT_ID,
      redirect_uri:
        Platform.OS == 'web' ? Config.CONNECT_HEALTH_RECORDS_CALLBACK_WEB : Config.CONNECT_HEALTH_RECORDS_CALLBACK,
      state: state,
      email: String(pEmail).length > 0 ? String(pEmail) : String(userAttributes.email), //'thang+5@kalibra.ai'
      scope: 'patient:read-all offline_access',
      first_name: String(firstName),
      last_name: String(lastName)
    });

    if (Platform.OS == 'web') {
      window.location.href = `${Config.CONNECT_HEALTH_RECORDS_AUTHORIZE_URL}?${params.toString()}`;
    } else {
      const authUrl = `${Config.CONNECT_HEALTH_RECORDS_AUTHORIZE_URL}?${params.toString()}`;
      navigation.navigate('CHCallback', { authUrl: authUrl });
    }
  };

  const t = useTranslationFunc(mainMenuContainer);
  const chatbotName = useGetChatbotName();
  // const isConnectHealthRecordsEnable =
  //   tenantFeatures?.find((item: TenantFeature) => item.key == 'ConnectHealthRecordsFeature') != undefined;

  let chConnectionMessage = 'Connection established succesfully!';
  let showSecondButton = false;

  if (patientMapping) {
    const { lastReportsSyncedAt, reportsSyncStatus } = patientMapping;

    // Currently syncing
    if (reportsSyncStatus === 'In Progress' || reportsSyncStatus === 'Pending') {
      chConnectionMessage = 'Connection is active. Currently fetching data.';
    }

    // Last attempt failed (and never succeeded before)
    else if (reportsSyncStatus === 'Failed' && !lastReportsSyncedAt) {
      chConnectionMessage = 'Connection is active, but some data are missing. Refresh?';
      showSecondButton = true;
    }

    // Has successful sync before
    else if (lastReportsSyncedAt) {
      chConnectionMessage = 'Connection is active. Refresh data?';
      showSecondButton = true;
    }
  }

  const specialGroupMenus: GroupMenuItem[] = [
    {
      leftIcon: <ActionIcon color={th['color-primary-500']} backgroundColor={th['color-primary-transparent-200']} />,
      name: t('mainMenuActions'),
      action: actionsHandler,
      hidden: false,
      count: newActionCount,
      id: 'menu-item-action'
    },
    {
      leftIcon: <ScanIcon color={th['color-primary-500']} backgroundColor={th['color-primary-transparent-200']} />,
      name: t('mainMenuScan'),
      action: scanHandler,
      hidden: appName == 'Kalibra' ? false : true,
      count: 0,
      id: 'menu-item-scan'
    },
    {
      leftIcon: (
        <AssessmentIcon color={th['color-primary-500']} backgroundColor={th['color-primary-transparent-200']} />
      ),
      name: t('mainMenuAssessments'),
      action: assessmentsHandler,
      hidden: false,
      count: newAssessmentCount,
      id: 'menu-item-assessments'
    },
    {
      leftIcon: (
        <HealthRecordsIcon color={th['color-primary-500']} backgroundColor={th['color-primary-transparent-200']} />
      ),
      name: 'Records',
      action: healthRecordsHandler,
      hidden: appName == 'Kalibra' ? false : true,
      count: 0,
      id: 'menu-item-health-records'
    },
    {
      leftIcon: <MarkerIcon color={th['color-primary-500']} backgroundColor={th['color-primary-transparent-200']} />,
      name: t('mainMenuMarkersOfInterest'),
      action: markersHandler,
      hidden: false,
      count: 0,
      id: 'menu-item-markers'
    },
    {
      leftIcon: (
        <TalkToKaliIcon color={th['color-primary-500']} backgroundColor={th['color-primary-transparent-200']} />
      ),
      name: t('mainMenuTalkToKali', { chatbot_name: chatbotName }),
      action: chatHandler,
      hidden: false,
      count: newMessageCount,
      id: 'menu-item-chatbot'
    }
  ];

  const groupMenu: GroupMenuItem[] = [
    {
      name: t('mainMenuProfile'),
      action: profileHandler,
      hidden: false,
      id: 'menu-item-profile',
      accessoryLeft: 'person-outline'
    },
    // {
    //   name: t('mainMenuSubscriptions'),
    //   action: subscriptionsHandler,
    //   hidden: appName == 'Kalibra' ? false : true,
    //   id: 'menu-item-subscription',
    //   accessoryLeft: 'credit-card-outline'
    // },
    { name: t('mainMenuAccount'), action: accountHandler, hidden: true, id: 'menu-item-account' },
    {
      name: t('mainMenuDataSharing'),
      action: dataSharingHandler,
      hidden: !isDataSharingEnabled,
      id: 'menu-item-datasharing',
      accessoryLeft: 'people-outline'
    },
    {
      name: t('mainManuConnectHealthRecords'),
      action: connectHealthRecordsHandler,
      hidden: appName == 'Kalibra' ? false : true,
      id: 'menu-item-cnnect-health-records',
      accessoryLeft: 'clipboard-outline'
    },
    {
      name: t('mainMenuWearables'),
      action: wearablesHandler,
      hidden: false,
      id: 'menu-item-wearables',
      accessoryLeft: 'bluetooth-outline'
    },
    {
      name: t('mainMenuPreferences'),
      action: preferencesHandler,
      hidden: false,
      id: 'menu-item-preferences',
      accessoryLeft: 'color-palette-outline'
    },
    {
      name: t('mainMenuLogOut'),
      action: handleSignOut,
      hidden: false,
      id: 'menu-item-logout',
      accessoryLeft: 'log-out-outline'
    },
    {
      name: t('mainMenuDeveloperSettings'),
      action: developerSettingsHandler,
      hidden: !Config.DEVELOPMENT_MODE,
      id: 'menu-item-devsettings',
      accessoryLeft: 'settings-2-outline'
    }
  ];

  if (isLatestDataCountLoading || isLoading) {
    return <Spinner visible={true} />;
  }

  //view
  return (
    <>
      <ScrollView alwaysBounceVertical={true} style={styleContainer.scrollView}>
        <SpecialGroupMenu items={specialGroupMenus} />
        <GroupMenu items={groupMenu} />
        <BottomMenu onAboutKalibra={aboutUsHandler} onFeedback={feedbackHandler} />
      </ScrollView>
      <ErrorModal
        visible={latestDataCountError != undefined || updateLatestDataCountError != undefined}
        message={{ ...latestDataCountError, ...updateLatestDataCountError }}
      />
      <InfoModal
        visible={visibleConnectedSuccesfulCHModal}
        message={'Connection established succesfully!'}
        closeBtnClick={() => {
          setVisibleConnectedSuccesfulCHModal(false);
        }}
      />
      {showSecondButton == true ? (
        <InfoModal
          visible={visibleAlreadyConnectedCHModal}
          message={chConnectionMessage}
          secondaryButtonTitle="No"
          buttonTitle="Yes"
          secondaryButtonClick={() => {
            setVisibleAlreadyConnectedCHModal(false);
          }}
          closeBtnClick={async () => {
            setVisibleAlreadyConnectedCHModal(false);
            const patientMapping = await ConsolidateHealthService.syncPatientReports();
            setPatientMapping(patientMapping);
          }}
        />
      ) : (
        <InfoModal
          visible={visibleAlreadyConnectedCHModal}
          message={chConnectionMessage}
          closeBtnClick={() => {
            setVisibleAlreadyConnectedCHModal(false);
          }}
        />
      )}
    </>
  );
};

export default MainMenuContainer;
